import { FC, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { FormTabMenu } from "../FormTabMenu/FormTabMenu";

import { EventRowsTimeline } from "./EventRowsTimeline/EventRowsTimeline";
import { groupEventRowsByDay } from "./helpers/groupEventRowsByDay";
import { EventsTab, EventsTabPage, useEventRowsTab } from "./helpers/useEventRowsTab";
import { UpcomingCountBadge } from "./UpcomingCountBadge/UpcomingCountBadge";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ChevronDown, Date as UpcomingEventsIcon, Success as PastEventsIcon } from "@/icons/outlined";

interface OrganizationPageEventsProps {
  organizationId: string;
  upcomingEvents: EventsTabPage;
  pastEvents: EventsTabPage;
}

export const OrganizationPageEvents: FC<OrganizationPageEventsProps> = ({
  organizationId,
  upcomingEvents,
  pastEvents,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isPhoneScreen } = useMediaQuery();
  const hasUpcomingEvents = upcomingEvents.rows.length > 0;
  const hasPastEvents = pastEvents.rows.length > 0;
  const [selectedTab, setSelectedTab] = useState<EventsTab>(hasUpcomingEvents ? "upcoming" : "past");
  const currentTab = getDisplayableTab(selectedTab, { hasUpcomingEvents, hasPastEvents });

  const upcomingRows = useEventRowsTab({ organizationId, tab: "upcoming", firstPage: upcomingEvents });
  const pastRows = useEventRowsTab({ organizationId, tab: "past", firstPage: pastEvents });
  const currentRows = currentTab === "upcoming" ? upcomingRows : pastRows;

  if (!hasUpcomingEvents && !hasPastEvents) {
    return null;
  }

  const days = groupEventRowsByDay({ rows: currentRows.visibleRows, eventsById: currentRows.eventsById, locale });
  const tabs = [
    ...(hasUpcomingEvents
      ? [
          {
            label: t("organization", "upcoming"),
            value: "upcoming" as const,
            icon: isPhoneScreen ? <UpcomingEventsIcon /> : undefined,
            badge: isPhoneScreen ? undefined : <UpcomingCountBadge count={upcomingRows.totalCount} />,
          },
        ]
      : []),
    ...(hasPastEvents
      ? [
          {
            label: t("organization", "past"),
            value: "past" as const,
            icon: isPhoneScreen ? <PastEventsIcon /> : undefined,
          },
        ]
      : []),
  ];
  const hasBothTabs = tabs.length > 1;
  const desktopTitle = hasUpcomingEvents ? t("organization", "events") : t("organization", "events.past");
  const mobileTitle =
    currentTab === "upcoming" ? t("organization", "events.upcoming") : t("organization", "events.past");
  const tabMenu = hasBothTabs ? (
    <FormTabMenu<EventsTab> currentTab={currentTab} onChange={setSelectedTab} tabs={tabs} />
  ) : null;

  return (
    <Stack gap={{ xs: 2, sm: 3 }} data-test="org-page-events">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ display: { sm: "none" } }}>
        <FormTypography variant="h6" vibe="brand" sx={{ textTransform: "uppercase" }}>
          {mobileTitle}
        </FormTypography>
        <Box>{tabMenu}</Box>
      </Stack>

      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
        }}
      >
        <Box>{tabMenu}</Box>
        <FormTypography variant="h6" vibe="brand" sx={{ textTransform: "uppercase", textAlign: "center" }}>
          {desktopTitle}
        </FormTypography>
        <Box />
      </Box>

      <EventRowsTimeline key={currentTab} days={days} tab={currentTab} />

      {currentRows.hasMoreRows ? (
        <Stack direction="row" justifyContent="center" sx={{ marginTop: { xs: 3, sm: 2 } }}>
          <FormButton
            variant="outline"
            size="medium"
            onClick={currentRows.showMoreRows}
            isLoading={currentRows.isLoadingMoreRows}
            endIcon={<ChevronDown />}
            data-test="org-page-events-see-more"
          >
            {t("common", "seeMore")}
          </FormButton>
        </Stack>
      ) : null}
    </Stack>
  );
};

const getDisplayableTab = (
  selectedTab: EventsTab,
  { hasUpcomingEvents, hasPastEvents }: { hasUpcomingEvents: boolean; hasPastEvents: boolean }
): EventsTab => {
  if (selectedTab === "upcoming" && !hasUpcomingEvents && hasPastEvents) {
    return "past";
  }
  if (selectedTab === "past" && !hasPastEvents && hasUpcomingEvents) {
    return "upcoming";
  }
  return selectedTab;
};
